import { PurchasesEntitlementInfo, PurchasesEntitlementInfos } from '@inkitt/contracts-common';
import { SubscriptionEntitlements } from '@inkitt/types-galatea';
import differenceInDays from 'date-fns/differenceInDays';

export type LatestUnlimitedSubscription = {
  productId: string;
  productPlanId?: string;
  expirationDate: string;
  // Note: this value can be negative if the expiration date is in the future
  daysSinceExpiration: number;
};

type InfoWithUnsubscribeEvent = Omit<PurchasesEntitlementInfo, 'unsubscribeDetectedAtMillis'> & {
  unsubscribeDetectedAtMillis: number;
};

type InfoWithExpirationDate = Omit<PurchasesEntitlementInfo, 'expirationDateMillis' | 'expirationDate'> & {
  expirationDateMillis: number;
  expirationDate: string;
};

const unlimitedEntitlementLiterals = new Set<string>([
  SubscriptionEntitlements.unlimited,
  SubscriptionEntitlements.subscription,
]);

// TODO: Move this to libs/utils-revcat
export const revcatUtils = {
  getLatestUnlimitedSubscriptionInfo: (
    entitlements: PurchasesEntitlementInfos,
  ): LatestUnlimitedSubscription | undefined => {
    const matchingEntitlement = Object.values(entitlements.all)
      .filter((info) => unlimitedEntitlementLiterals.has(info.identifier))
      .filter((info): info is InfoWithExpirationDate => !!info.expirationDate && !!info.expirationDateMillis)
      .sort((a, b) => b.expirationDateMillis - a.expirationDateMillis)[0];

    if (matchingEntitlement) {
      const daysSinceExpiration = differenceInDays(new Date(), new Date(matchingEntitlement.expirationDate));

      return {
        productId: matchingEntitlement.productIdentifier,
        productPlanId: matchingEntitlement.productPlanIdentifier ?? undefined,
        expirationDate: matchingEntitlement.expirationDate,
        // Note: this value can be negative if the expiration date is in the future
        daysSinceExpiration,
      };
    }

    return;
  },

  hasActiveUnlimitedSubscription: (entitlements: PurchasesEntitlementInfos): boolean => {
    return (
      Object.values(entitlements.active).findIndex((entitlement) =>
        ([SubscriptionEntitlements.unlimited, SubscriptionEntitlements.subscription] as string[]).includes(
          entitlement.identifier,
        ),
      ) > -1
    );
  },

  getActiveContentEntitlement: (entitlements: PurchasesEntitlementInfos) => {
    return Object.values(entitlements.active).find((entitlement) =>
      ([SubscriptionEntitlements.unlimited, SubscriptionEntitlements.subscription] as string[]).includes(
        entitlement.identifier,
      ),
    );
  },

  activeUnlimitedProductId: (entitlements: PurchasesEntitlementInfos): string | undefined => {
    const activeUnlimitedEntitlement = Object.values(entitlements.active).find((entitlement) =>
      ([SubscriptionEntitlements.unlimited, SubscriptionEntitlements.subscription] as string[]).includes(
        entitlement.identifier,
      ),
    );

    return activeUnlimitedEntitlement?.productIdentifier;
  },

  getLatestUnsubscribeEvent: (entitlements?: PurchasesEntitlementInfos) => {
    const allEntitlements = Object.values(entitlements?.all ?? {});
    const latestUnsubscribeEventMillis =
      allEntitlements
        .filter((e): e is InfoWithUnsubscribeEvent => e.unsubscribeDetectedAtMillis !== null)
        .sort((a, b) => b.unsubscribeDetectedAtMillis - a.unsubscribeDetectedAtMillis)[0]
        ?.unsubscribeDetectedAtMillis ?? undefined;
    const daysSinceLatestUnsubscribe =
      latestUnsubscribeEventMillis && differenceInDays(new Date(), new Date(latestUnsubscribeEventMillis));
    return { latestUnsubscribeEventMillis, daysSinceLatestUnsubscribe };
  },

  willActiveUnlimitedSubscriptionRenew: (entitlements?: PurchasesEntitlementInfos) => {
    const currentEntitlement = Object.values(entitlements?.active ?? {}).find((entitlement) =>
      unlimitedEntitlementLiterals.has(entitlement.identifier),
    );

    return currentEntitlement && currentEntitlement.willRenew;
  },
};
